import { useState, useEffect } from "react";
import { Bell, BellRing, X, Heart, MessageCircle, Star, Shield } from "lucide-react";
import api from "@/lib/api";

/**
 * NotificationPrompt
 * - Prompts user ONCE on their very first login for notification permissions.
 * - Stores decision in localStorage keyed by user_id so it NEVER asks on future logins.
 */
export default function NotificationPrompt({ user }) {
  const [visible, setVisible] = useState(false);
  const [state, setState] = useState("idle"); // idle | loading | success | denied

  const storageKey = user?.user_id 
    ? `jjodi_notif_prompt_dismissed_${user.user_id}` 
    : "jjodi_notif_prompt_dismissed_guest";

  useEffect(() => {
    if (!user) return;
    if (!("Notification" in window)) return;

    // If browser permission is ALREADY decided (granted or denied), remember and DO NOT show
    if (Notification.permission !== "default") {
      localStorage.setItem(storageKey, "true");
      return;
    }

    // Check if user already saw or acted on the prompt previously
    const alreadyPrompted = localStorage.getItem(storageKey);
    if (alreadyPrompted === "true") return;

    // Show ONCE after a 2-second delay on first login
    const t = setTimeout(() => setVisible(true), 2000);
    return () => clearTimeout(t);
  }, [user, storageKey]);

  const handleAllow = async () => {
    setState("loading");
    // Permanently remember so it never shows again
    localStorage.setItem(storageKey, "true");

    try {
      const { requestFcmToken, setupForegroundNotifications } = await import("../lib/firebase");
      const token = await requestFcmToken(true);

      if (Notification.permission === "granted") {
        setState("success");
        setupForegroundNotifications();
        if (token) {
          api.post("/notifications/save-token", { token }).catch(() => {});
        }
        setTimeout(() => setVisible(false), 1500);
      } else {
        setState("denied");
        setTimeout(() => setVisible(false), 2000);
      }
    } catch (err) {
      console.error("NotificationPrompt error:", err);
      setVisible(false);
    }
  };

  const handleDismiss = () => {
    // Permanently remember so it never shows again
    localStorage.setItem(storageKey, "true");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[9998] bg-black/40 backdrop-blur-sm"
        onClick={handleDismiss}
        aria-hidden="true"
      />

      {/* Popup Card */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Enable Notifications"
        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] w-[92vw] max-w-sm
                   bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl border border-border/60
                   overflow-hidden animate-in slide-in-from-bottom-8 fade-in duration-400 font-sans"
      >
        {/* Top gradient banner */}
        <div className="relative h-28 bg-gradient-to-br from-[#6b1a24] via-[#9b2c3a] to-[#c0392b] flex items-center justify-center overflow-hidden">
          {/* Decorative circles */}
          <div className="absolute -top-6 -right-6 w-28 h-28 rounded-full bg-white/10" />
          <div className="absolute -bottom-8 -left-6 w-24 h-24 rounded-full bg-white/10" />

          {/* Bell icon with animation */}
          <div className="relative z-10 w-16 h-16 rounded-full bg-white/20 flex items-center justify-center ring-4 ring-white/20 shadow-lg">
            {state === "success" ? (
              <span className="text-3xl animate-bounce">🎉</span>
            ) : state === "denied" ? (
              <span className="text-3xl">😔</span>
            ) : (
              <BellRing className="w-8 h-8 text-white animate-[wiggle_1s_ease-in-out_infinite]" />
            )}
          </div>

          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4 text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          {state === "success" ? (
            <div className="text-center py-2">
              <p className="text-lg font-bold text-emerald-600">Notifications enabled! 🎊</p>
              <p className="text-sm text-muted-foreground mt-1">You will now receive match alerts instantly.</p>
            </div>
          ) : state === "denied" ? (
            <div className="text-center py-2">
              <p className="text-base font-semibold text-foreground">Permission Not Granted</p>
              <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                You can enable notifications anytime in Browser Settings:<br />
                <span className="font-medium text-[#8F000D]">Settings → Notifications → Allow</span>
              </p>
            </div>
          ) : (
            <>
              <h2 className="text-lg font-bold text-foreground text-center leading-snug">
                Enable Match Notifications 🔔
              </h2>
              <p className="text-xs text-muted-foreground text-center mt-1.5 leading-relaxed">
                Get real-time updates for new profile interests and messages.
              </p>

              {/* Feature list */}
              <div className="mt-4 space-y-2.5">
                <FeatureRow icon={<Heart className="w-4 h-4 text-rose-500" />} text="Instant alerts for new interest requests" />
                <FeatureRow icon={<MessageCircle className="w-4 h-4 text-blue-500" />} text="Real-time chat notification alerts" />
                <FeatureRow icon={<Star className="w-4 h-4 text-amber-500" />} text="Daily AI match recommendations" />
              </div>

              {/* Buttons */}
              <div className="flex gap-3 mt-5">
                <button
                  onClick={handleDismiss}
                  className="flex-1 py-2.5 rounded-2xl border border-border text-sm text-muted-foreground hover:bg-muted transition-colors font-medium"
                >
                  Later
                </button>
                <button
                  onClick={handleAllow}
                  disabled={state === "loading"}
                  className="flex-1 py-2.5 rounded-2xl text-sm font-bold text-white
                             bg-[#8F000D] hover:bg-[#70000A]
                             disabled:opacity-70 transition-all shadow-md hover:shadow-lg
                             flex items-center justify-center gap-2"
                >
                  {state === "loading" ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Enabling...
                    </>
                  ) : (
                    <>
                      <Bell className="w-4 h-4" />
                      Allow
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes wiggle {
          0%, 100% { transform: rotate(-8deg); }
          50% { transform: rotate(8deg); }
        }
      `}</style>
    </>
  );
}

function FeatureRow({ icon, text }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 w-6 h-6 rounded-full bg-muted flex items-center justify-center shrink-0">
        {icon}
      </div>
      <p className="text-[12px] text-foreground leading-snug">{text}</p>
    </div>
  );
}
